import UnreadIndicator from '@atoms/UnreadIndicator';
import HeaderButton from '@molecules/HeaderButton';
import type { DrawerContentComponentProps } from '@react-navigation/drawer';
import { DrawerContentScrollView } from '@react-navigation/drawer';
import { Colors, TextBold, TextReg } from '@sunrise-ui/primitives';
import { defaultBack, routerGoBackOrReplace } from '@utils/navigation';
import { router, usePathname, type Href } from 'expo-router';
import { Drawer } from 'expo-router/drawer';
import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { PlusSignIcon } from '@hugeicons/core-free-icons';
import HeaderDrawerButton from '@molecules/HeaderDrawerButton';
import { headerOptionsCreator } from '@utils/screenOptions';

const CustomDrawerItem = ({
  path,
  label,
  numUnread,
}: {
  path: Href;
  label: string;
  numUnread?: number;
}) => {
  const currentPath = usePathname();
  const isActivePath = currentPath.startsWith(path as string);

  return (
    <Pressable
      style={[styles.navItem, isActivePath && styles.activeNavItem]}
      onPress={() => {
        router.navigate(path);
      }}
    >
      <View style={styles.navItemContent}>
        {isActivePath ? (
          <TextBold style={styles.navItemText}>{label}</TextBold>
        ) : (
          <TextReg style={styles.navItemText}>{label}</TextReg>
        )}
        <UnreadIndicator count={numUnread} />
      </View>
    </Pressable>
  );
};

const CustomDrawerContent = (props: DrawerContentComponentProps) => {
  return (
    <DrawerContentScrollView {...props} style={styles.drawerContent}>
      <View style={styles.sectionHeaderContainer}>
        <TextReg style={styles.sectionHeaderText}>My Intros</TextReg>
      </View>
      <CustomDrawerItem path="/intros/myIntros/pending" label="Pending" />
      <CustomDrawerItem path="/intros/myIntros/accepted" label="Accepted" />
      <CustomDrawerItem path="/intros/myIntros/denied" label="Denied" />

      <View style={styles.sectionHeaderContainer}>
        <TextReg style={styles.sectionHeaderText}>Intros I Made</TextReg>
      </View>
      <CustomDrawerItem path="/intros/introsMade/pending" label="Pending" />
      <CustomDrawerItem path="/intros/introsMade/accepted" label="Accepted" />
      <CustomDrawerItem path="/intros/introsMade/denied" label="Denied" />
    </DrawerContentScrollView>
  );
};

export default function Layout() {
  const screenOptions = {
    drawerActiveTintColor: Colors.text,
    drawerType: 'front' as const,
    overlayColor: Colors.blackOpacity05,
    drawerStyle: {
      backgroundColor: Colors.purple0,
      borderRadius: 0,
    },
  };

  return (
    <Drawer
      screenOptions={screenOptions}
      backBehavior="history"
      drawerContent={(props) => <CustomDrawerContent {...props} />}
    >
      <Drawer.Screen
        name="introsMade/accepted"
        options={headerOptionsCreator(
          HeaderDrawerButton,
          <HeaderButton
            icon={PlusSignIcon}
            onPress={() => {
              router.navigate('/introduce');
            }}
          />,
        )}
      />
      <Drawer.Screen
        name="introsMade/denied"
        options={headerOptionsCreator(
          HeaderDrawerButton,
          <HeaderButton
            icon={PlusSignIcon}
            onPress={() => {
              router.navigate('/introduce');
            }}
          />,
        )}
      />
      <Drawer.Screen
        name="introsMade/pending"
        options={headerOptionsCreator(
          HeaderDrawerButton,
          <HeaderButton
            icon={PlusSignIcon}
            onPress={() => {
              router.navigate('/introduce');
            }}
          />,
        )}
      />
      <Drawer.Screen
        name="myIntros/accepted"
        options={{ headerShown: false }}
      />
      <Drawer.Screen name="myIntros/denied" options={{ headerShown: false }} />
      <Drawer.Screen name="myIntros/pending" options={{ headerShown: false }} />
      <Drawer.Screen
        name="myIntros/prep/[introId]"
        options={headerOptionsCreator(
          <HeaderButton onPress={defaultBack('/intros/myIntros/pending')} />,
        )}
      />
      <Drawer.Screen
        name="myIntros/preview/[introId]"
        options={headerOptionsCreator(
          <HeaderButton
            onPress={(params) => {
              routerGoBackOrReplace(
                `/intros/myIntros/prep/${params.introId as string}`,
              );
            }}
          />,
        )}
      />
      <Drawer.Screen
        name="myIntros/record/[introId]"
        options={headerOptionsCreator(
          <HeaderButton
            onPress={(params) => {
              routerGoBackOrReplace(
                `/intros/myIntros/prep/${params.introId as string}`,
              );
            }}
          />,
        )}
      />
    </Drawer>
  );
}

const styles = StyleSheet.create({
  drawerContent: {
    flex: 1,
  },
  sectionHeaderContainer: {
    paddingHorizontal: 24,
    paddingBottom: 8,
    marginTop: 16,
  },
  sectionHeaderText: {
    fontSize: 16,
    color: Colors.text,
  },
  navItem: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    marginHorizontal: 12,
    marginVertical: 2,
    borderRadius: 8,
  },
  activeNavItem: {
    backgroundColor: Colors.purple1opacity50,
  },
  navItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  navItemText: {
    fontSize: 14,
    color: Colors.text,
  },
});
